import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import { DarkMode, LightMode } from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { useThemeContext } from '../context/ThemeContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentPlaylistId, playlistInfo, clearPlaylist, error } = useAppContext();
  const { mode, toggleTheme } = useThemeContext();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const currentPath = location.pathname;

  const navigateToPage = (path: string) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <>
      {/* Sticky Logo Overlay with burger menu */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          py: 2.5,
          px: 4,
          background: isDark ? 'rgba(15, 15, 35, 0.85)' : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Burger menu button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Open navigation menu"
          style={{
            position: 'absolute',
            left: '16px',
            padding: '8px 12px',
            borderRadius: '8px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
            fontSize: '20px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: theme.palette.text.primary,
            letterSpacing: '-0.01em',
            fontFamily: '"Inter", sans-serif',
          }}
        >
          emotify
        </Typography>

        {/* Dark mode toggle */}
        <IconButton
          onClick={toggleTheme}
          aria-label="Toggle dark mode"
          sx={{
            position: 'absolute',
            right: 16,
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
          }}
        >
          {mode === 'light' ? <DarkMode /> : <LightMode />}
        </IconButton>
      </Box>

      {/* Overlay for sidebar */}
      <Box
        onClick={() => setSidebarOpen(false)}
        aria-hidden={!sidebarOpen}
        sx={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: sidebarOpen ? 'blur(2px)' : 'none',
          zIndex: 999,
          opacity: sidebarOpen ? 1 : 0,
          pointerEvents: sidebarOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      />
      
      {/* Sidebar */}
      <Box
        sx={{
          width: '280px',
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          borderRight: `1px solid ${theme.palette.divider}`,
          background: isDark ? '#1B1B30' : '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          py: 4,
          px: 3,
          zIndex: 1000,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform',
          pt: '80px',
        }}
      >
        {/* Logo - clickable to go to landing page */}
        <Box
          onClick={() => navigateToPage('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            mb: 6,
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.7,
            },
          }}
        >
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: theme.palette.text.primary,
              letterSpacing: '-0.01em',
              fontFamily: '"Inter", sans-serif',
            }}
          >
            emotify
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 1, 
          mb: 'auto',
          width: '100%',
        }}>
          <button
            onClick={() => navigateToPage('/')}
            title="Home"
            style={{
              padding: '10px 20px',
              borderRadius: '100px',
              border: 'none',
              background: currentPath === '/' ? (isDark ? '#fff' : '#000') : 'transparent',
              color: currentPath === '/' ? (isDark ? '#000' : '#fff') : (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'),
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              textAlign: 'center',
              fontFamily: 'inherit',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (currentPath !== '/') {
                e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
              }
            }}
            onMouseLeave={(e) => {
              if (currentPath !== '/') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            Home
          </button>
          <button
            onClick={() => navigateToPage('/about')}
            title="About"
            style={{
              padding: '10px 20px',
              borderRadius: '100px',
              border: 'none',
              background: currentPath === '/about' ? (isDark ? '#fff' : '#000') : 'transparent',
              color: currentPath === '/about' ? (isDark ? '#000' : '#fff') : (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'),
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              textAlign: 'center',
              fontFamily: 'inherit',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (currentPath !== '/about') {
                e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
              }
            }}
            onMouseLeave={(e) => {
              if (currentPath !== '/about') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            About
          </button>
          <button
            onClick={() => navigateToPage('/contact')}
            title="Contact"
            style={{
              padding: '10px 20px',
              borderRadius: '100px',
              border: 'none',
              background: currentPath === '/contact' ? (isDark ? '#fff' : '#000') : 'transparent',
              color: currentPath === '/contact' ? (isDark ? '#000' : '#fff') : (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'),
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              textAlign: 'center',
              fontFamily: 'inherit',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (currentPath !== '/contact') {
                e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
              }
            }}
            onMouseLeave={(e) => {
              if (currentPath !== '/contact') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            Contact
          </button>
        </Box>

        {/* Playlist Info - only when viewing a successful result (not the error screen) */}
        {currentPlaylistId && currentPath === '/results' && !error && (
          <>
            <Box
              sx={{
                pt: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
              }}
            >
              {playlistInfo?.image && (
                <Box
                  component="img"
                  src={playlistInfo.image}
                  alt={playlistInfo.name}
                  sx={{ width: 48, height: 48, borderRadius: '8px', objectFit: 'cover', mb: 1 }}
                />
              )}
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {playlistInfo?.name || 'Playlist analyzed'}
              </Typography>
              {playlistInfo?.owner && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  by {playlistInfo.owner}
                </Typography>
              )}
            </Box>
            
            {/* Analyze Another Playlist Button */}
            <button
              onClick={() => {
                clearPlaylist();
                navigate('/');
              }}
              title="Analyze another playlist"
              style={{
                marginTop: '12px',
                padding: '10px 20px',
                borderRadius: '100px',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                background: 'transparent',
                color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
                textAlign: 'center',
                fontFamily: 'inherit',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Analyze Another
            </button>
          </>
        )}
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          minHeight: '100vh',
          background: theme.palette.background.default,
          pt: '64px',
        }}
      >
        {children}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 4,
          px: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.default,
        }}
      >
        <Box
          component="nav"
          aria-label="Footer"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: 1.5, sm: 3 },
          }}
        >
          {[
            { label: 'Home', path: '/' },
            { label: 'About', path: '/about' },
            { label: 'How it works', path: '/methodology' },
            { label: 'Contact', path: '/contact' },
            { label: 'Privacy', path: '/privacy' },
            { label: 'Terms', path: '/terms' },
          ].map(({ label, path }) => (
            <Typography
              key={path}
              component="a"
              href={path}
              onClick={(e) => { e.preventDefault(); navigateToPage(path); }}
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                '&:hover': { color: theme.palette.text.primary },
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>
        <Typography
          sx={{
            fontSize: '13px',
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          made with ♪ by btarifi · © 2026 emotify
        </Typography>
      </Box>
    </>
  );
};

export default Layout;
